import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  BedDouble, CalendarDays, LogIn, LogOut, Wrench, LayoutGrid,
  ClipboardList, Plus, X, ChevronRight, ChevronLeft, Phone,
  User, MapPin, Star, Wifi, Tv, AirVent, Coffee, Utensils,
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock,
  DollarSign, TrendingUp, Home, Settings, BarChart2, Search,
  Sparkles, ShowerHead, Brush, Layers, Minimize2, Zap, Rocket
} from 'lucide-react';
import { LunaVoiceModel, LunaFace, type LunaVoiceModelHandle } from './LunaVoiceModel';
import BillingLockGate from './billing/BillingLockGate';
import StoreSettingsFAB from './billing/StoreSettingsFAB';
import NetworkMonitor from './NetworkMonitor';
import MarketingCenter from './MarketingCenter';
import VoiceCheckInHologram from './VoiceCheckInHologram';
import RoomStatusHologram from './RoomStatusHologram';

// ─── Types ────────────────────────────────────────────────────────────────────
type RoomStatus = 'AVAILABLE' | 'RESERVED' | 'OCCUPIED' | 'CLEANING' | 'MAINTENANCE';
type RoomType = 'SINGLE' | 'DOUBLE' | 'TWIN' | 'SUITE' | 'FAMILY' | 'DELUXE';
type ReservationStatus = 'PENDING' | 'CONFIRMED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'CANCELLED' | 'NO_SHOW';

interface HotelRoom {
  id: number; storeId: number; roomNumber: string; type: RoomType;
  floor: number; capacity: number; baseRatePerNight: string;
  amenities: string[]; status: RoomStatus; imageUrl?: string;
  isActive: boolean; description?: string;
}
interface Guest { id: number; name: string; phone: string; email?: string; }
interface HotelExtra { id: number; description: string; quantity: string; unitPrice: string; total: string; addedAt: string; voided: boolean; }
interface HotelReservation {
  id: number; reservationRef: string; roomId: number; customerId?: number;
  status: ReservationStatus; checkInDate: string; checkOutDate: string;
  actualCheckIn?: string; actualCheckOut?: string; nights: number;
  adults: number; children: number; ratePerNight: string; subtotal: string;
  extraChargesTotal: string; discountAmt: string; grandTotal: string;
  amountPaid: string; paymentStatus: string; paymentMethod?: string;
  source: string; specialRequests?: string; notes?: string;
  Room?: HotelRoom; Guest?: Guest; Extras?: HotelExtra[];
}
interface DashboardStats {
  totalRooms: number; occupied: number; available: number; cleaning: number;
  maintenance: number; reserved: number; occupancyRate: number;
  arrivalsToday: number; departuresToday: number; overstays: number;
  revenueToday: number; revenueThisMonth: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<RoomStatus, { label: string; color: string; bg: string; border: string; dot: string }> = {
  AVAILABLE:   { label: 'Available',   color: 'text-emerald-400', bg: 'bg-emerald-500/10',  border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
  RESERVED:    { label: 'Reserved',    color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10',  border: 'border-fuchsia-500/30', dot: 'bg-fuchsia-400' },
  OCCUPIED:    { label: 'Occupied',    color: 'text-cyan-400',    bg: 'bg-cyan-500/10',     border: 'border-cyan-500/30',    dot: 'bg-cyan-400' },
  CLEANING:    { label: 'Cleaning',    color: 'text-yellow-400',  bg: 'bg-yellow-500/10',   border: 'border-yellow-500/30',  dot: 'bg-yellow-400' },
  MAINTENANCE: { label: 'Maintenance', color: 'text-red-400',     bg: 'bg-red-500/10',      border: 'border-red-500/30',     dot: 'bg-red-400' },
};
const RES_STATUS_CONFIG: Record<ReservationStatus, { label: string; color: string }> = {
  PENDING:     { label: 'Pending',     color: 'text-yellow-400' },
  CONFIRMED:   { label: 'Confirmed',   color: 'text-blue-400' },
  CHECKED_IN:  { label: 'In House',    color: 'text-cyan-400' },
  CHECKED_OUT: { label: 'Checked Out', color: 'text-emerald-400' },
  CANCELLED:   { label: 'Cancelled',   color: 'text-red-400' },
  NO_SHOW:     { label: 'No Show',     color: 'text-slate-500' },
};
const AMENITY_ICON: Record<string, any> = {
  wifi: Wifi, tv: Tv, ac: AirVent, coffee: Coffee, breakfast: Utensils,
  shower: ShowerHead, minibar: DollarSign,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const apiHeaders = (apiKey: string, token?: string | null) => {
  const h: Record<string, string> = { 'Content-Type': 'application/json', 'x-store-api-key': apiKey };
  const t = token || localStorage.getItem('kangavena:cashierToken');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
};
const fmt = (n: number | string) => `Rs.${parseFloat(String(n)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// ─── Sub-components ───────────────────────────────────────────────────────────
const GlassCard = ({ children, className = '' }: { children: React.ReactNode; className?: string; key?: React.Key }) => (
  <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl ${className}`}>{children}</div>
);

const StatBadge = ({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color: string }) => (
  <div className="flex flex-col gap-0.5 px-4 py-2.5 bg-white/5 rounded-xl border border-white/8">
    <div className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${color}`}>
      <Icon size={11} /> {label}
    </div>
    <span className="text-xl font-bold text-white">{value}</span>
  </div>
);

const RoomCard = ({ room, onClick }: { room: HotelRoom; onClick: () => void; key?: React.Key }) => {
  const cfg = STATUS_CONFIG[room.status];
  return (
    <motion.button
      whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className={`relative flex flex-col gap-1 p-3 rounded-xl border ${cfg.bg} ${cfg.border} text-left w-full transition-all`}
    >
      <div className="flex justify-between items-start">
        <span className="text-lg font-black text-white">{room.roomNumber}</span>
        <span className={`w-2.5 h-2.5 rounded-full mt-1 ${cfg.dot}`} />
      </div>
      <span className="text-[10px] font-mono text-slate-400 uppercase">{room.type}</span>
      <span className={`text-[10px] font-semibold ${cfg.color}`}>{cfg.label}</span>
      <span className="text-[9px] text-slate-500 mt-0.5">{fmt(room.baseRatePerNight)}/night</span>
    </motion.button>
  );
};

const ReservationStatusBadge = ({ status }: { status: ReservationStatus }) => {
  const cfg = RES_STATUS_CONFIG[status];
  return <span className={`text-[10px] font-semibold font-mono uppercase ${cfg.color}`}>{cfg.label}</span>;
};

// ─── Main Component ───────────────────────────────────────────────────────────
interface HotelDashboardProps {
  storeApiKey: string;
  storeName: string;
  onSwitchShop?: () => void;
  onExit?: () => void;
}

type ActiveView = 'FLOOR_PLAN' | 'BOOKINGS' | 'NEW_BOOKING' | 'CHECK_IN' | 'CHECK_OUT' | 'ROOMS' | 'REPORTS';

const HotelDashboard = ({ storeApiKey, storeName, onSwitchShop, onExit }: HotelDashboardProps) => {
  const [activeView, setActiveView] = useState<ActiveView>('FLOOR_PLAN');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [rooms, setRooms] = useState<HotelRoom[]>([]);
  const [reservations, setReservations] = useState<HotelReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<HotelRoom | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<HotelReservation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [lunaOpen, setLunaOpen] = useState(false);
  const lunaRef = useRef<LunaVoiceModelHandle | null>(null);
  const [marketingCenterOpen, setMarketingCenterOpen] = useState(false);
  const [voiceCheckInOpen, setVoiceCheckInOpen] = useState(false);
  const [roomStatusOpen, setRoomStatusOpen] = useState(false);

  const token = localStorage.getItem('kangavena:cashierToken');
  const headers = apiHeaders(storeApiKey, token);

  const showToast = (msg: string) => { setToastMsg(msg); setTimeout(() => setToastMsg(null), 3000); };
  const showError = (msg: string) => { setError(msg); setTimeout(() => setError(null), 5000); };

  // Change C: Luna voice mode — reverse open events dispatched by LunaVoiceModel priming handlers
  useEffect(() => {
    const onCheckInOpen = () => setVoiceCheckInOpen(true);
    const onRoomStatusOpen = () => setRoomStatusOpen(true);
    window.addEventListener('luna-hotel-checkin-open', onCheckInOpen);
    window.addEventListener('luna-hotel-roomstatus-open', onRoomStatusOpen);
    return () => {
      window.removeEventListener('luna-hotel-checkin-open', onCheckInOpen);
      window.removeEventListener('luna-hotel-roomstatus-open', onRoomStatusOpen);
    };
  }, []);

  // ── VPN Heartbeat: re-check IP every 15s while Luna is open ──
  useEffect(() => {
    if (!lunaOpen) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/luna/heartbeat');
        const data = await res.json();
        if (!data.allowed) {
          setLunaOpen(false);
          lunaRef.current?.forceKill();
        }
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [lunaOpen]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/hotel/dashboard', { headers });
      if (res.ok) setStats(await res.json());
    } catch { /* silent */ }
  }, [storeApiKey]);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await fetch('/api/hotel/rooms', { headers });
      if (res.ok) setRooms(await res.json());
    } catch { /* silent */ }
  }, [storeApiKey]);

  const fetchReservations = useCallback(async (status?: string) => {
    try {
      const url = status ? `/api/hotel/reservations?status=${status}` : '/api/hotel/reservations';
      const res = await fetch(url, { headers });
      if (res.ok) setReservations(await res.json());
    } catch { /* silent */ }
  }, [storeApiKey]);

  useEffect(() => { fetchStats(); fetchRooms(); fetchReservations(); }, [fetchStats, fetchRooms, fetchReservations]);
  useEffect(() => {
    if (activeView === 'CHECK_IN') fetchReservations('CONFIRMED');
    else if (activeView === 'CHECK_OUT') fetchReservations('CHECKED_IN');
    else if (activeView === 'BOOKINGS') fetchReservations();
    else if (activeView === 'FLOOR_PLAN') { fetchRooms(); fetchStats(); }
  }, [activeView]);

  useEffect(() => {
    const handler = async (e: Event) => {
      const { action, roomNumber, newStatus, guestName, nights } = (e as CustomEvent).detail || {};
      if (action === 'ROOM_STATUS_UPDATE' && roomNumber) {
        const room = rooms.find(r => r.roomNumber === String(roomNumber));
        if (room && newStatus) {
          try {
            const r = await fetch(`/api/hotel/rooms/${room.id}/status`, { method: 'PATCH', headers, body: JSON.stringify({ status: newStatus, reason: 'Luna voice command' }) });
            if (r.ok) { fetchRooms(); fetchStats(); showToast(`Luna: Room ${roomNumber} → ${newStatus}`); }
            else showError(`Luna: Failed to update room ${roomNumber}`);
          } catch { showError('Luna: Room update failed'); }
        } else if (!room) { showError(`Luna: Room ${roomNumber} not found`); }
      } else if (action === 'QUICK_CHECKIN' && roomNumber) {
        setActiveView('NEW_BOOKING');
        showToast(`Luna: Quick check-in for room ${roomNumber}${guestName ? ` — ${guestName}` : ''}. Fill in the form.`);
      }
    };
    window.addEventListener('luna-hotel-action', handler);
    return () => window.removeEventListener('luna-hotel-action', handler);
  }, [rooms, headers]);

  const navTabs: { id: ActiveView; label: string; icon: any }[] = [
    { id: 'FLOOR_PLAN',   label: 'Floor Plan',  icon: LayoutGrid },
    { id: 'BOOKINGS',     label: 'Bookings',    icon: ClipboardList },
    { id: 'NEW_BOOKING',  label: 'New Booking', icon: Plus },
    { id: 'CHECK_IN',     label: 'Check-In',    icon: LogIn },
    { id: 'CHECK_OUT',    label: 'Check-Out',   icon: LogOut },
    { id: 'ROOMS',        label: 'Rooms',       icon: Settings },
  ];

  return (
    <BillingLockGate apiKey={storeApiKey}>
    <div className="min-h-screen bg-[#020408] text-white flex flex-col">
      {/* Toast */}
      <AnimatePresence>
        {toastMsg && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 px-6 py-3 rounded-2xl text-sm font-semibold backdrop-blur-xl shadow-xl">
            <CheckCircle size={14} className="inline mr-2" />{toastMsg}
          </motion.div>
        )}
        {error && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] bg-red-500/20 border border-red-500/40 text-red-300 px-6 py-3 rounded-2xl text-sm font-semibold backdrop-blur-xl shadow-xl">
            <XCircle size={14} className="inline mr-2" />{error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/8 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
              <BedDouble size={18} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-[10px] font-mono text-cyan-400/60 uppercase tracking-widest">Hotel POS</p>
              <p className="text-sm font-bold text-white leading-tight">{storeName}</p>
            </div>
          </div>

          {/* Stats strip */}
          {stats && (
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              <StatBadge icon={BedDouble}     label="Occupied"  value={`${stats.occupied}/${stats.totalRooms}`} color="text-cyan-400" />
              <StatBadge icon={LogIn}          label="Arrivals"  value={stats.arrivalsToday}   color="text-fuchsia-400" />
              <StatBadge icon={LogOut}         label="Departures" value={stats.departuresToday} color="text-yellow-400" />
              {stats.overstays > 0 && <StatBadge icon={AlertTriangle} label="Overstays" value={stats.overstays} color="text-red-400" />}
              <StatBadge icon={TrendingUp}     label="Today Rev" value={`Rs.${(stats.revenueToday/1000).toFixed(1)}k`} color="text-emerald-400" />
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => setMarketingCenterOpen(true)}
              title="Marketing Center"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-cyan-400 hover:text-white border border-cyan-500/30 rounded-lg hover:bg-cyan-500/15 transition-all font-bold"
            >
              <Rocket size={13} /><span className="hidden sm:inline">Marketing</span>
            </button>
            {onSwitchShop && (
              <button onClick={onSwitchShop} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-cyan-400 hover:text-white border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10 transition-all" title="Switch Store">
                <Layers size={13} /><span className="hidden sm:inline font-medium">Stores</span>
              </button>
            )}
            <button onClick={() => { fetchStats(); fetchRooms(); fetchReservations(); }} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-all">
              <RefreshCw size={15} />
            </button>
            {onExit && <button onClick={onExit} className="px-3 py-1.5 text-xs text-slate-400 hover:text-white border border-white/10 rounded-lg hover:bg-white/5 transition-all">Exit</button>}
          </div>
        </div>

        {/* Nav tabs — desktop only; mobile uses fixed bottom nav */}
        <div className="max-w-7xl mx-auto hidden md:flex items-center gap-1 mt-3 pb-1">
          {navTabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${activeView === tab.id ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <tab.icon size={13} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content — bottom padding clears both the mobile nav and dock */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-4 pb-[148px] md:pb-[80px]">
        <AnimatePresence mode="wait">
          <motion.div key={activeView} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>

            {/* FLOOR PLAN */}
            {activeView === 'FLOOR_PLAN' && (
              <>
                {/* Mobile analytics — desktop header shows these (hidden md:flex); surface them here on mobile */}
                {stats && (
                  <div className="md:hidden grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-3.5">
                      <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Occupancy</p>
                      <p className="text-2xl font-black text-cyan-400 leading-none">{stats.occupancyRate.toFixed(0)}%</p>
                      <p className="text-[10px] text-slate-500 mt-1">{stats.occupied}/{stats.totalRooms} rooms</p>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-3.5">
                      <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Today Revenue</p>
                      <p className="text-2xl font-black text-emerald-400 leading-none">Rs.{(stats.revenueToday/1000).toFixed(1)}k</p>
                      <p className="text-[10px] text-slate-500 mt-1">Month: Rs.{(stats.revenueThisMonth/1000).toFixed(0)}k</p>
                    </div>
                    <div className={`border rounded-2xl p-3.5 ${stats.arrivalsToday > 0 ? 'bg-fuchsia-500/10 border-fuchsia-500/20' : 'bg-white/5 border-white/10'}`}>
                      <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Arrivals Today</p>
                      <p className="text-2xl font-black text-fuchsia-400 leading-none">{stats.arrivalsToday}</p>
                      {stats.overstays > 0 && <p className="text-[10px] text-red-400 mt-1">{stats.overstays} overstay{stats.overstays !== 1 ? 's' : ''} ⚠</p>}
                      {stats.overstays === 0 && <p className="text-[10px] text-slate-500 mt-1">No overstays</p>}
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-3.5">
                      <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Departures</p>
                      <p className="text-2xl font-black text-yellow-400 leading-none">{stats.departuresToday}</p>
                      <p className="text-[10px] text-slate-500 mt-1">{stats.cleaning} room{stats.cleaning !== 1 ? 's' : ''} cleaning</p>
                    </div>
                  </div>
                )}
                <FloorPlanView rooms={rooms} onRoomClick={r => { setSelectedRoom(r); }} onRoomUpdated={() => { fetchRooms(); fetchStats(); }} apiHeaders={headers} showToast={showToast} showError={showError} />
              </>
            )}

            {/* BOOKINGS */}
            {activeView === 'BOOKINGS' && (
              <BookingsView reservations={reservations} onSelect={r => setSelectedReservation(r)} onRefresh={() => fetchReservations()} headers={headers} showToast={showToast} showError={showError} />
            )}

            {/* NEW BOOKING */}
            {activeView === 'NEW_BOOKING' && (
              <BookingWizard storeApiKey={storeApiKey} headers={headers} onSuccess={() => { showToast('Reservation created!'); setActiveView('BOOKINGS'); fetchReservations(); fetchRooms(); fetchStats(); }} showError={showError} />
            )}

            {/* CHECK IN */}
            {activeView === 'CHECK_IN' && (
              <CheckInView reservations={reservations} headers={headers} showToast={showToast} showError={showError} onRefresh={() => { fetchReservations('CONFIRMED'); fetchRooms(); fetchStats(); }} />
            )}

            {/* CHECK OUT */}
            {activeView === 'CHECK_OUT' && (
              <CheckOutView reservations={reservations} headers={headers} showToast={showToast} showError={showError} onRefresh={() => { fetchReservations('CHECKED_IN'); fetchRooms(); fetchStats(); }} />
            )}

            {/* ROOMS MANAGEMENT */}
            {activeView === 'ROOMS' && (
              <RoomsManagementView rooms={rooms} headers={headers} showToast={showToast} showError={showError} onRefresh={() => { fetchRooms(); fetchStats(); }} />
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* Room Detail Modal */}
      <AnimatePresence>
        {selectedRoom && (
          <RoomDetailModal room={selectedRoom} headers={headers} onClose={() => setSelectedRoom(null)} showToast={showToast} showError={showError} onUpdated={() => { fetchRooms(); fetchStats(); setSelectedRoom(null); }} />
        )}
      </AnimatePresence>

      {/* Reservation Detail Modal */}
      <AnimatePresence>
        {selectedReservation && (
          <ReservationDetailModal reservation={selectedReservation} headers={headers} onClose={() => setSelectedReservation(null)} showToast={showToast} showError={showError} onUpdated={() => { fetchReservations(); setSelectedReservation(null); }} />
        )}
      </AnimatePresence>
      {/* ══ VOICE CHECK-IN HOLOGRAM ═══════════════════════════ */}
      <VoiceCheckInHologram
        isOpen={voiceCheckInOpen}
        onClose={() => { setVoiceCheckInOpen(false); lunaRef.current?.forceKill(); window.dispatchEvent(new CustomEvent('luna-hotel-mode-exit')); }}
        storeApiKey={storeApiKey}
        rooms={rooms}
        onCheckInCreated={() => { fetchReservations(); fetchRooms(); fetchStats(); showToast('Reservation created via Luna!'); }}
      />

      {/* ══ ROOM STATUS HOLOGRAM ════════════════════════════════ */}
      <RoomStatusHologram
        isOpen={roomStatusOpen}
        onClose={() => { setRoomStatusOpen(false); lunaRef.current?.forceKill(); window.dispatchEvent(new CustomEvent('luna-hotel-mode-exit')); }}
        storeApiKey={storeApiKey}
        rooms={rooms}
        onRoomsUpdated={() => { fetchRooms(); fetchStats(); showToast('Rooms updated via Luna!'); }}
      />

      {/* ══ LUNA VOICE MODEL ════════════════════════════════ */}
      <div className={`fixed inset-0 z-[9998] print:hidden ${lunaOpen ? '' : 'opacity-0 pointer-events-none [&_*]:pointer-events-none'}`}>
        <LunaVoiceModel
          ref={lunaRef}
          apiKey={storeApiKey}
          role="store"
          shopType="HOTEL"
          storeName={storeName}
          onDisconnect={() => setLunaOpen(false)}
        />
      </div>
      {/* Luna Overlay backdrop */}
      <AnimatePresence>
        {lunaOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9997] pointer-events-none" />
        )}
      </AnimatePresence>
      {/* Minimize button — outside transform stacking context */}
      <AnimatePresence>
        {lunaOpen && (
          <motion.button
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            onClick={() => setLunaOpen(false)}
            className="fixed top-4 right-4 z-[99999] flex items-center gap-2 px-3.5 py-2 rounded-xl bg-black/70 backdrop-blur-xl border border-white/10 text-slate-300 text-[11px] font-bold hover:border-red-500/30 hover:text-red-400 transition-all pointer-events-auto">
            <Minimize2 size={12} /> Minimize Luna
          </motion.button>
        )}
      </AnimatePresence>

      {/* ══ FLOATING LUNA BUBBLE (above dock) ═══════════════ */}
      {!lunaOpen && !voiceCheckInOpen && !roomStatusOpen && (
        <div className="fixed bottom-[84px] left-4 md:bottom-[80px] z-50 w-12 h-12">
          <motion.div animate={{ scale: [1, 1.18, 1], opacity: [0.25, 0.08, 0.25] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0 rounded-full border border-cyan-400/50 scale-[1.45] pointer-events-none" style={{ zIndex: -1 }} />
          <motion.div animate={{ scale: [1, 1.28, 1], opacity: [0.15, 0.04, 0.15] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            className="absolute inset-0 rounded-full border border-cyan-300/40 scale-[1.75] pointer-events-none" style={{ zIndex: -1 }} />
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => setLunaOpen(true)} title="Open Luna AI"
            className="relative w-12 h-12 rounded-full bg-[#020408]/90 backdrop-blur-md shadow-[0_8px_20px_rgba(6,182,212,0.2)] flex items-center justify-center border border-cyan-500/40 hover:scale-105 active:scale-95 transition-all cursor-pointer overflow-hidden">
            <LunaFace sentiment="neutral" isListening={false} className="w-10 h-10 text-cyan-400" />
          </motion.button>
        </div>
      )}

      {/* ══ FLOATING PING (above dock, right) ═════════════════ */}
      {!lunaOpen && !voiceCheckInOpen && !roomStatusOpen && (
        <div className="fixed bottom-[84px] right-4 md:bottom-[80px] z-50 flex flex-col items-center justify-center gap-0.5 w-12 h-12 rounded-full bg-[#020408]/90 backdrop-blur-md border border-white/10 shadow-md cursor-pointer">
          <NetworkMonitor />
        </div>
      )}

      {/* ══ BOTTOM DOCK — always visible, matches POS / AutoParts pattern ══ */}
      {!lunaOpen && !voiceCheckInOpen && !roomStatusOpen && (
        <div className="print:hidden fixed bottom-0 left-0 right-0 h-[72px] bg-black/80 backdrop-blur-xl border-t border-white/10 px-4 flex items-center justify-center z-40 shadow-[0_-8px_24px_rgba(6,182,212,0.06)]">
          <div className="w-full max-w-md flex gap-3">
            <motion.button whileTap={{ scale: 0.95 }}
              onClick={() => window.dispatchEvent(new CustomEvent('luna-hotel-express-checkin-start'))}
              title="Express Check-In via Luna voice"
              className="flex-1 h-12 rounded-xl bg-cyan-500/10 text-cyan-400 font-bold text-[13px] border border-cyan-500/30 hover:bg-cyan-500/20 shadow-sm transition-all flex items-center justify-center gap-2 active:scale-95">
              <LogIn size={15} className="text-cyan-400" />
              <span>Quick Check-In</span>
            </motion.button>
            <motion.button whileTap={{ scale: 0.95 }}
              onClick={() => window.dispatchEvent(new CustomEvent('luna-hotel-quick-status-start'))}
              title="Batch room status update via Luna voice"
              className="flex-1 h-12 rounded-xl bg-fuchsia-500 text-white font-black text-[13px] border border-fuchsia-400 hover:bg-fuchsia-400 shadow-[0_4px_12px_rgba(217,70,239,0.3)] transition-all flex items-center justify-center gap-2 active:scale-95">
              <Home size={15} className="text-white" />
              <span>Room Status</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* ── Mobile Nav tabs (hidden on md+, sits above the dock) ── */}
      <nav className="fixed bottom-[72px] left-0 right-0 z-50 md:hidden bg-[#020408]/90 backdrop-blur-xl border-t border-white/10">
        <div className="flex items-stretch justify-around">
          {navTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 px-1 transition-all ${
                activeView === tab.id
                  ? 'text-cyan-400 border-t-2 border-cyan-400'
                  : 'text-slate-600 border-t-2 border-transparent'
              }`}
            >
              <tab.icon size={18} strokeWidth={activeView === tab.id ? 2.5 : 1.5} />
              <span className="text-[8px] font-bold uppercase tracking-tight leading-tight mt-0.5">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
      {/* 💳 BILLING FAB */}
      <StoreSettingsFAB apiKey={storeApiKey} storeName={storeName} />

      {/* 🚀 MARKETING CENTER */}
      <MarketingCenter
        isOpen={marketingCenterOpen}
        onClose={() => setMarketingCenterOpen(false)}
        storeApiKey={storeApiKey}
        storeName={storeName}
        onTriggerLunaConsult={() => {
          setLunaOpen(true);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('kangavena:luna-prompt', { detail: { text: 'ව්‍යාපාර දියුණු කරන්නේ කෙසේද?' } }));
          }, 600);
        }}
        onReplayPlan={(plan) => {
          setLunaOpen(true);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('kangavena:replay-strategic-plan', { detail: plan }));
          }, 80);
        }}
      />
    </div>
    </BillingLockGate>
  );
};

// ─── Floor Plan View ──────────────────────────────────────────────────────────
const FloorPlanView = ({ rooms, onRoomClick, onRoomUpdated, apiHeaders: headers, showToast, showError }: {
  rooms: HotelRoom[]; onRoomClick: (r: HotelRoom) => void;
  onRoomUpdated: () => void; apiHeaders: Record<string,string>;
  showToast: (m: string) => void; showError: (m: string) => void;
}) => {
  const floors = [...new Set(rooms.map(r => r.floor))].sort((a, b) => a - b);
  const statusCounts = rooms.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {} as Record<string, number>);

  return (
    <div className="flex flex-col gap-6">
      {/* Legend */}
      <GlassCard className="p-4 flex flex-wrap gap-3">
        {Object.entries(STATUS_CONFIG).map(([s, cfg]) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
            <span className={`text-xs font-mono ${cfg.color}`}>{cfg.label}</span>
            <span className="text-xs text-slate-500">({statusCounts[s] || 0})</span>
          </div>
        ))}
      </GlassCard>

      {/* Floors */}
      {floors.map(floor => (
        <div key={floor}>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-3">Floor {floor}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {rooms.filter(r => r.floor === floor).map(room => (
              <RoomCard key={room.id} room={room} onClick={() => onRoomClick(room)} />
            ))}
          </div>
        </div>
      ))}

      {rooms.length === 0 && (
        <GlassCard className="p-12 text-center">
          <BedDouble size={40} className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No rooms configured yet.</p>
          <button onClick={() => {}} className="mt-3 text-xs text-cyan-400 hover:text-cyan-300">Go to Rooms Management →</button>
        </GlassCard>
      )}
    </div>
  );
};

// ─── Bookings View ────────────────────────────────────────────────────────────
const BookingsView = ({ reservations, onSelect, onRefresh, headers, showToast, showError }: {
  reservations: HotelReservation[]; onSelect: (r: HotelReservation) => void;
  onRefresh: () => void; headers: Record<string,string>;
  showToast: (m: string) => void; showError: (m: string) => void;
}) => {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filtered = reservations.filter(r => {
    const matchesText = !filter || r.reservationRef.toLowerCase().includes(filter.toLowerCase()) || r.Guest?.name?.toLowerCase().includes(filter.toLowerCase()) || r.Room?.roomNumber?.includes(filter);
    const matchesStatus = !statusFilter || r.status === statusFilter;
    return matchesText && matchesStatus;
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search ref, guest, room…"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-500/50" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/50">
          <option value="">All Status</option>
          {Object.entries(RES_STATUS_CONFIG).map(([s, c]) => <option key={s} value={s}>{c.label}</option>)}
        </select>
      </div>

      <GlassCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Ref', 'Room', 'Guest', 'Check-In', 'Check-Out', 'Nights', 'Total', 'Status'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-mono text-slate-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} onClick={() => onSelect(r)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-cyan-400">{r.reservationRef}</td>
                  <td className="px-4 py-3 text-white font-semibold">{r.Room?.roomNumber || '-'}</td>
                  <td className="px-4 py-3">
                    <p className="text-white text-xs">{r.Guest?.name || 'Walk-in'}</p>
                    {r.Guest?.phone && <p className="text-slate-500 text-[10px]">{r.Guest.phone}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300">{fmtDate(r.checkInDate)}</td>
                  <td className="px-4 py-3 text-xs text-slate-300">{fmtDate(r.checkOutDate)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{r.nights}n</td>
                  <td className="px-4 py-3 text-xs text-white font-semibold">{fmt(r.grandTotal)}</td>
                  <td className="px-4 py-3"><ReservationStatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList size={30} className="mx-auto mb-2 text-slate-700" />
              No reservations found.
            </div>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

// ─── Booking Wizard ───────────────────────────────────────────────────────────
const BookingWizard = ({ storeApiKey, headers, onSuccess, showError }: {
  storeApiKey: string; headers: Record<string,string>;
  onSuccess: () => void; showError: (m: string) => void;
}) => {
  const [step, setStep] = useState(1);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [availableRooms, setAvailableRooms] = useState<HotelRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<HotelRoom | null>(null);
  const [guestSearch, setGuestSearch] = useState('');
  const [guests, setGuests] = useState<Guest[]>([]);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestPhone, setNewGuestPhone] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [specialRequests, setSpecialRequests] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discountAmt, setDiscountAmt] = useState('0');
  const [submitting, setSubmitting] = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(false);

  const today = new Date().toISOString().slice(0,10);

  const searchAvailability = async () => {
    if (!checkIn || !checkOut || checkIn >= checkOut) { showError('Select valid check-in/out dates'); return; }
    setLoadingAvail(true);
    try {
      const r = await fetch(`/api/hotel/availability?checkIn=${checkIn}&checkOut=${checkOut}`, { headers });
      if (!r.ok) throw new Error((await r.json()).error);
      setAvailableRooms(await r.json());
    } catch (e: any) { showError(e.message); }
    finally { setLoadingAvail(false); }
  };

  const searchGuests = async (q: string) => {
    if (!q || q.length < 2) { setGuests([]); return; }
    try {
      const r = await fetch(`/api/customers?search=${encodeURIComponent(q)}`, { headers });
      if (r.ok) { const d = await r.json(); setGuests(Array.isArray(d) ? d : d.customers || []); }
    } catch { /* silent */ }
  };

  const nights = checkIn && checkOut ? Math.max(1, Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000)) : 0;
  const subtotal = selectedRoom ? nights * parseFloat(selectedRoom.baseRatePerNight) : 0;
  const grandTotal = Math.max(0, subtotal - parseFloat(discountAmt || '0'));

  const createBooking = async () => {
    setSubmitting(true);
    try {
      let customerId: number | null = selectedGuest?.id || null;

      if (!selectedGuest && newGuestName && newGuestPhone) {
        const cr = await fetch('/api/customers', { method: 'POST', headers, body: JSON.stringify({ name: newGuestName, phone: newGuestPhone }) });
        if (!cr.ok) throw new Error((await cr.json()).error || 'Failed to create guest');
        const cd = await cr.json();
        customerId = cd.id || cd.customer?.id;
      }

      const body = { roomId: selectedRoom!.id, customerId, checkInDate: checkIn, checkOutDate: checkOut, adults, children, specialRequests, paymentMethod, discountAmt: parseFloat(discountAmt) || 0 };
      const r = await fetch('/api/hotel/reservations', { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to create reservation');
      onSuccess();
    } catch (e: any) { showError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1,2,3].map(s => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${s === step ? 'bg-cyan-500 text-black' : s < step ? 'bg-emerald-500/30 text-emerald-400' : 'bg-white/10 text-slate-500'}`}>
              {s < step ? '✓' : s}
            </div>
            {s < 3 && <div className={`flex-1 h-px w-16 ${s < step ? 'bg-emerald-500/50' : 'bg-white/10'}`} />}
          </div>
        ))}
        <span className="ml-2 text-xs text-slate-400">{['Select Room', 'Guest Details', 'Confirm'][step-1]}</span>
      </div>

      <GlassCard className="p-6">
        {/* Step 1 */}
        {step === 1 && (
          <div className="flex flex-col gap-5">
            <h2 className="text-base font-bold text-white flex items-center gap-2"><CalendarDays size={16} className="text-cyan-400" /> Select Dates & Room</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Check-In</label>
                <input type="date" min={today} value={checkIn} onChange={e => setCheckIn(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
              <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Check-Out</label>
                <input type="date" min={checkIn || today} value={checkOut} onChange={e => setCheckOut(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
            </div>
            <button onClick={searchAvailability} disabled={loadingAvail || !checkIn || !checkOut}
              className="w-full py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 rounded-xl text-sm font-semibold transition-all disabled:opacity-40">
              {loadingAvail ? 'Searching…' : 'Check Availability'}
            </button>
            {availableRooms.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {availableRooms.map(r => (
                  <button key={r.id} onClick={() => setSelectedRoom(r)}
                    className={`p-3 rounded-xl border text-left transition-all ${selectedRoom?.id === r.id ? 'border-cyan-500/60 bg-cyan-500/20' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                    <p className="font-black text-white text-lg">{r.roomNumber}</p>
                    <p className="text-[10px] text-slate-400 uppercase font-mono">{r.type}</p>
                    <p className="text-xs text-cyan-400 mt-1 font-semibold">{fmt(r.baseRatePerNight)}<span className="text-slate-500 font-normal">/night</span></p>
                  </button>
                ))}
              </div>
            )}
            {availableRooms.length === 0 && checkIn && checkOut && !loadingAvail && (
              <p className="text-center text-slate-500 text-sm py-4">Click "Check Availability" to see available rooms.</p>
            )}
            <button onClick={() => setStep(2)} disabled={!selectedRoom}
              className="w-full py-2.5 bg-cyan-500 hover:bg-cyan-400 text-black rounded-xl text-sm font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed">
              Continue <ChevronRight size={14} className="inline" />
            </button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="flex flex-col gap-5">
            <h2 className="text-base font-bold text-white flex items-center gap-2"><User size={16} className="text-cyan-400" /> Guest Details</h2>
            <div>
              <label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Search Existing Guest</label>
              <input value={guestSearch} onChange={e => { setGuestSearch(e.target.value); searchGuests(e.target.value); }}
                placeholder="Name or phone number…"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-500/50" />
              {guests.length > 0 && (
                <div className="mt-2 bg-black/60 border border-white/10 rounded-xl overflow-hidden">
                  {guests.slice(0,5).map(g => (
                    <button key={g.id} onClick={() => { setSelectedGuest(g); setGuests([]); setGuestSearch(g.name); }}
                      className={`w-full px-4 py-2.5 text-left hover:bg-white/5 flex items-center justify-between ${selectedGuest?.id === g.id ? 'bg-cyan-500/10' : ''}`}>
                      <span className="text-sm text-white">{g.name}</span>
                      <span className="text-xs text-slate-400">{g.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!selectedGuest && (
              <>
                <div className="flex items-center gap-3"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-slate-500">or create new</span><div className="flex-1 h-px bg-white/10" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Full Name</label>
                    <input value={newGuestName} onChange={e => setNewGuestName(e.target.value)} placeholder="Guest name"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-500/50" /></div>
                  <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Phone</label>
                    <input value={newGuestPhone} onChange={e => setNewGuestPhone(e.target.value)} placeholder="+94 …"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-500/50" /></div>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Adults</label>
                <input type="number" min={1} value={adults} onChange={e => setAdults(+e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
              <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Children</label>
                <input type="number" min={0} value={children} onChange={e => setChildren(+e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
            </div>
            <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Special Requests</label>
              <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)} rows={2} placeholder="Late check-in, extra pillows…"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none resize-none focus:border-cyan-500/50" /></div>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 py-2.5 border border-white/10 text-slate-300 rounded-xl text-sm font-semibold hover:bg-white/5 transition-all">
                <ChevronLeft size={14} className="inline" /> Back
              </button>
              <button onClick={() => setStep(3)} disabled={!selectedGuest && (!newGuestName || !newGuestPhone)}
                className="flex-1 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-black rounded-xl text-sm font-bold transition-all disabled:opacity-30">
                Continue <ChevronRight size={14} className="inline" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && selectedRoom && (
          <div className="flex flex-col gap-4">
            <h2 className="text-base font-bold text-white flex items-center gap-2"><Sparkles size={16} className="text-cyan-400" /> Confirm Booking</h2>
            <div className="bg-white/5 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Room</span><span className="text-white font-semibold">{selectedRoom.roomNumber} — {selectedRoom.type}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Guest</span><span className="text-white">{selectedGuest?.name || newGuestName}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Check-In</span><span className="text-white">{fmtDate(checkIn)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Check-Out</span><span className="text-white">{fmtDate(checkOut)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Nights</span><span className="text-white">{nights}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-slate-400">Rate/Night</span><span className="text-cyan-400">{fmt(selectedRoom.baseRatePerNight)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="text-white">{fmt(subtotal)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Discount (Rs.)</label>
                <input type="number" min={0} value={discountAmt} onChange={e => setDiscountAmt(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
              <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Payment Method</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-500/50">
                  <option value="cash">Cash</option><option value="card">Card</option><option value="mobile">Mobile</option><option value="split">Split</option>
                </select></div>
            </div>
            <div className="flex justify-between items-center bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-3">
              <span className="text-sm font-semibold text-slate-300">Grand Total</span>
              <span className="text-xl font-black text-cyan-300">{fmt(grandTotal)}</span>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 py-2.5 border border-white/10 text-slate-300 rounded-xl text-sm font-semibold hover:bg-white/5 transition-all">
                <ChevronLeft size={14} className="inline" /> Back
              </button>
              <button onClick={createBooking} disabled={submitting}
                className="flex-1 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-black rounded-xl text-sm font-bold transition-all disabled:opacity-40">
                {submitting ? 'Creating…' : 'Confirm Booking'}
              </button>
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

// ─── Check-In View ────────────────────────────────────────────────────────────
const CheckInView = ({ reservations, headers, showToast, showError, onRefresh }: {
  reservations: HotelReservation[]; headers: Record<string,string>;
  showToast: (m: string) => void; showError: (m: string) => void; onRefresh: () => void;
}) => {
  const [checkingIn, setCheckingIn] = useState<number | null>(null);

  const doCheckIn = async (id: number) => {
    setCheckingIn(id);
    try {
      const r = await fetch(`/api/hotel/reservations/${id}/checkin`, { method: 'POST', headers, body: JSON.stringify({}) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      showToast('Guest checked in!');
      onRefresh();
    } catch (e: any) { showError(e.message); }
    finally { setCheckingIn(null); }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Today's Arrivals — {reservations.length} guest(s)</p>
      {reservations.length === 0 && (
        <GlassCard className="p-12 text-center"><LogIn size={30} className="mx-auto mb-2 text-slate-700" /><p className="text-slate-500">No arrivals expected today.</p></GlassCard>
      )}
      <div className="grid gap-3">
        {reservations.map(res => (
          <GlassCard key={res.id} className="p-4 flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-cyan-400">{res.reservationRef}</span>
                <ReservationStatusBadge status={res.status} />
              </div>
              <p className="text-white font-semibold">{res.Guest?.name || 'Walk-in Guest'}</p>
              <p className="text-xs text-slate-400 mt-0.5">{res.Guest?.phone} — Room {res.Room?.roomNumber} ({res.Room?.type})</p>
              <p className="text-xs text-slate-500">Planned: {fmtDate(res.checkInDate)} → {fmtDate(res.checkOutDate)} ({res.nights}n)</p>
              {res.specialRequests && <p className="text-xs text-yellow-400/80 mt-1"><AlertTriangle size={10} className="inline mr-1" />{res.specialRequests}</p>}
            </div>
            <button onClick={() => doCheckIn(res.id)} disabled={checkingIn === res.id || !res.customerId}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-xl text-sm font-bold hover:bg-emerald-500/30 transition-all disabled:opacity-40 whitespace-nowrap">
              {checkingIn === res.id ? 'Checking in…' : <><LogIn size={14} /> Check In</>}
            </button>
          </GlassCard>
        ))}
      </div>
    </div>
  );
};

// ─── Check-Out View ───────────────────────────────────────────────────────────
const CheckOutView = ({ reservations, headers, showToast, showError, onRefresh }: {
  reservations: HotelReservation[]; headers: Record<string,string>;
  showToast: (m: string) => void; showError: (m: string) => void; onRefresh: () => void;
}) => {
  const [selected, setSelected] = useState<HotelReservation | null>(null);
  const [extras, setExtras] = useState<HotelExtra[]>([]);
  const [addingExtra, setAddingExtra] = useState(false);
  const [extraDesc, setExtraDesc] = useState('');
  const [extraQty, setExtraQty] = useState('1');
  const [extraPrice, setExtraPrice] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);
  const today = new Date().toISOString().slice(0,10);

  const selectReservation = async (r: HotelReservation) => {
    setSelected(r);
    const res = await fetch(`/api/hotel/reservations/${r.id}/extras`, { headers });
    if (res.ok) setExtras((await res.json()).filter((e: HotelExtra) => !e.voided));
    else setExtras([]);
    setAmountPaid('');
  };

  const addExtra = async () => {
    if (!selected || !extraDesc || !extraPrice) return;
    try {
      const r = await fetch(`/api/hotel/reservations/${selected.id}/extras`, { method: 'POST', headers, body: JSON.stringify({ description: extraDesc, quantity: parseFloat(extraQty), unitPrice: parseFloat(extraPrice) }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setExtras(prev => [...prev, d]);
      setExtraDesc(''); setExtraQty('1'); setExtraPrice(''); setAddingExtra(false);
      showToast('Extra added');
    } catch (e: any) { showError(e.message); }
  };

  const extrasTotal = extras.reduce((s, e) => s + parseFloat(e.total), 0);
  const nights = selected ? Math.max(1, Math.ceil((new Date().getTime() - new Date(selected.checkInDate).getTime()) / 86400000)) : 0;
  const roomTotal = selected ? nights * parseFloat(selected.ratePerNight) : 0;
  const discount = selected ? parseFloat(selected.discountAmt) : 0;
  const grandTotal = roomTotal + extrasTotal - discount;

  const doCheckout = async () => {
    if (!selected) return;
    setCheckingOut(true);
    try {
      const r = await fetch(`/api/hotel/reservations/${selected.id}/checkout`, { method: 'POST', headers, body: JSON.stringify({ paymentMethod, amountPaid: parseFloat(amountPaid) || grandTotal }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      showToast(`Checkout complete — ${d.reservationRef}`);
      setSelected(null); setExtras([]);
      onRefresh();
    } catch (e: any) { showError(e.message); }
    finally { setCheckingOut(false); }
  };

  const isOverstay = (r: HotelReservation) => r.checkOutDate < today;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: In-house list */}
      <div className="flex flex-col gap-3">
        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{reservations.length} guest(s) in house</p>
        {reservations.length === 0 && (
          <GlassCard className="p-12 text-center"><LogOut size={30} className="mx-auto mb-2 text-slate-700" /><p className="text-slate-500">No guests currently checked in.</p></GlassCard>
        )}
        {reservations.map(res => (
          <button key={res.id} onClick={() => selectReservation(res)}
            className={`w-full text-left p-4 rounded-xl border transition-all ${selected?.id === res.id ? 'bg-cyan-500/10 border-cyan-500/40' : 'bg-white/3 border-white/8 hover:border-white/20'}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white font-semibold">{res.Guest?.name || 'Guest'}</p>
                <p className="text-xs text-slate-400">Room {res.Room?.roomNumber} · {res.nights}n planned</p>
              </div>
              {isOverstay(res) ? (
                <span className="text-[10px] text-red-400 font-mono bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">OVERSTAY</span>
              ) : (
                <span className="text-[10px] text-yellow-400 font-mono">Out: {fmtDate(res.checkOutDate)}</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Right: Folio */}
      {selected ? (
        <GlassCard className="p-5 flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-bold text-white">Guest Folio</h3>
            <span className="text-xs font-mono text-cyan-400">{selected.reservationRef}</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-400"><span>Room {selected.Room?.roomNumber} × {nights} night(s)</span><span className="text-white">{fmt(roomTotal)}</span></div>
            {extras.map(e => (
              <div key={e.id} className="flex justify-between text-slate-400"><span>{e.description} ×{e.quantity}</span><span className="text-white">{fmt(e.total)}</span></div>
            ))}
            {discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount</span><span>-{fmt(discount)}</span></div>}
            <div className="flex justify-between border-t border-white/10 pt-2 font-bold"><span className="text-white">Grand Total</span><span className="text-cyan-300 text-base">{fmt(grandTotal)}</span></div>
          </div>

          {/* Add Extra */}
          {addingExtra ? (
            <div className="bg-white/5 rounded-xl p-3 flex flex-col gap-2">
              <input value={extraDesc} onChange={e => setExtraDesc(e.target.value)} placeholder="Description (e.g. Breakfast)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={extraQty} onChange={e => setExtraQty(e.target.value)} placeholder="Qty"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                <input type="number" value={extraPrice} onChange={e => setExtraPrice(e.target.value)} placeholder="Unit Price"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </div>
              <div className="flex gap-2">
                <button onClick={addExtra} className="flex-1 py-2 bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 rounded-lg text-xs font-semibold">Add</button>
                <button onClick={() => setAddingExtra(false)} className="flex-1 py-2 bg-white/5 text-slate-400 border border-white/10 rounded-lg text-xs">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingExtra(true)} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"><Plus size={12} /> Add Extra Charge</button>
          )}

          {/* Payment */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Payment</label>
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none">
                <option value="cash">Cash</option><option value="card">Card</option><option value="mobile">Mobile</option>
              </select></div>
            <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Amount Paid</label>
              <input type="number" value={amountPaid} onChange={e => setAmountPaid(e.target.value)} placeholder={String(grandTotal.toFixed(2))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
          </div>

          <button onClick={doCheckout} disabled={checkingOut}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-black font-black rounded-xl text-sm hover:opacity-90 transition-all disabled:opacity-40">
            {checkingOut ? 'Processing…' : `Process Checkout — ${fmt(grandTotal)}`}
          </button>
        </GlassCard>
      ) : (
        <GlassCard className="p-12 text-center flex flex-col items-center justify-center">
          <LogOut size={30} className="text-slate-700 mb-2" />
          <p className="text-slate-500 text-sm">Select a guest to view their folio</p>
        </GlassCard>
      )}
    </div>
  );
};

// ─── Rooms Management View ────────────────────────────────────────────────────
const RoomsManagementView = ({ rooms, headers, showToast, showError, onRefresh }: {
  rooms: HotelRoom[]; headers: Record<string,string>;
  showToast: (m: string) => void; showError: (m: string) => void; onRefresh: () => void;
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editRoom, setEditRoom] = useState<HotelRoom | null>(null);
  const [form, setForm] = useState({ roomNumber: '', type: 'SINGLE', floor: '1', capacity: '2', baseRatePerNight: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const openCreate = () => { setEditRoom(null); setForm({ roomNumber: '', type: 'SINGLE', floor: '1', capacity: '2', baseRatePerNight: '', description: '' }); setShowForm(true); };
  const openEdit = (r: HotelRoom) => { setEditRoom(r); setForm({ roomNumber: r.roomNumber, type: r.type, floor: String(r.floor), capacity: String(r.capacity), baseRatePerNight: r.baseRatePerNight, description: r.description || '' }); setShowForm(true); };

  const saveRoom = async () => {
    if (!form.roomNumber || !form.baseRatePerNight) { showError('Room number and rate required'); return; }
    setSubmitting(true);
    try {
      const url = editRoom ? `/api/hotel/rooms/${editRoom.id}` : '/api/hotel/rooms';
      const method = editRoom ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify({ ...form, floor: parseInt(form.floor), capacity: parseInt(form.capacity), baseRatePerNight: parseFloat(form.baseRatePerNight) }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      showToast(editRoom ? 'Room updated' : 'Room created');
      setShowForm(false); onRefresh();
    } catch (e: any) { showError(e.message); }
    finally { setSubmitting(false); }
  };

  const deleteRoom = async (id: number) => {
    if (!confirm('Deactivate this room?')) return;
    try {
      const r = await fetch(`/api/hotel/rooms/${id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error((await r.json()).error);
      showToast('Room deactivated'); onRefresh();
    } catch (e: any) { showError(e.message); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{rooms.length} room(s) configured</p>
        <button onClick={openCreate} className="flex items-center gap-1.5 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 rounded-xl text-sm font-semibold hover:bg-cyan-500/30 transition-all">
          <Plus size={14} /> Add Room
        </button>
      </div>

      {showForm && (
        <GlassCard className="p-5">
          <h3 className="text-sm font-bold text-white mb-4">{editRoom ? 'Edit Room' : 'Create New Room'}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[{k:'roomNumber',l:'Room No.',ph:'101'},{k:'floor',l:'Floor',ph:'1'},{k:'capacity',l:'Capacity',ph:'2'}].map(({k,l,ph}) => (
              <div key={k}><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">{l}</label>
                <input value={(form as any)[k]} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} placeholder={ph}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
            ))}
            <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Type</label>
              <select value={form.type} onChange={e => setForm(p => ({...p,type:e.target.value}))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none">
                {['SINGLE','DOUBLE','TWIN','SUITE','FAMILY','DELUXE'].map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Rate/Night (Rs.)</label>
              <input type="number" value={form.baseRatePerNight} onChange={e => setForm(p => ({...p,baseRatePerNight:e.target.value}))} placeholder="5000"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
            <div className="sm:col-span-3"><label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Description</label>
              <input value={form.description} onChange={e => setForm(p => ({...p,description:e.target.value}))} placeholder="Sea view, corner room…"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/50" /></div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-white/10 text-slate-300 rounded-xl text-sm hover:bg-white/5 transition-all">Cancel</button>
            <button onClick={saveRoom} disabled={submitting} className="flex-1 py-2 bg-cyan-500 hover:bg-cyan-400 text-black rounded-xl text-sm font-bold disabled:opacity-40">{submitting ? 'Saving…' : 'Save Room'}</button>
          </div>
        </GlassCard>
      )}

      <GlassCard className="overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/8">
            {['Room','Type','Floor','Capacity','Rate/Night','Status',''].map(h => (
              <th key={h} className="px-4 py-3 text-left text-[10px] font-mono text-slate-500 uppercase tracking-widest">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rooms.map(r => {
              const cfg = STATUS_CONFIG[r.status];
              return (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="px-4 py-3 font-black text-white text-base">{r.roomNumber}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 uppercase">{r.type}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{r.floor}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{r.capacity}</td>
                  <td className="px-4 py-3 text-xs text-cyan-400 font-semibold">{fmt(r.baseRatePerNight)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-semibold font-mono uppercase ${cfg.color}`}>{cfg.label}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(r)} className="text-xs text-slate-400 hover:text-white px-2 py-1 border border-white/10 rounded-lg hover:bg-white/5 transition-all">Edit</button>
                      <button onClick={() => deleteRoom(r.id)} className="text-xs text-red-400 hover:text-red-300 px-2 py-1 border border-red-500/20 rounded-lg hover:bg-red-500/10 transition-all">Remove</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rooms.length === 0 && <div className="text-center py-12 text-slate-500 text-sm">No rooms yet. Click "Add Room" to configure your first room.</div>}
      </GlassCard>
    </div>
  );
};

// ─── Room Detail Modal ─────────────────────────────────────────────────────────
const RoomDetailModal = ({ room, headers, onClose, showToast, showError, onUpdated }: {
  room: HotelRoom; headers: Record<string,string>; onClose: () => void;
  showToast: (m: string) => void; showError: (m: string) => void; onUpdated: () => void;
}) => {
  const cfg = STATUS_CONFIG[room.status];
  const [newStatus, setNewStatus] = useState(room.status);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const updateStatus = async () => {
    if (newStatus === room.status) { onClose(); return; }
    setSaving(true);
    try {
      const r = await fetch(`/api/hotel/rooms/${room.id}/status`, { method: 'PATCH', headers, body: JSON.stringify({ status: newStatus, reason }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      showToast(`Room ${room.roomNumber} → ${newStatus}`);
      onUpdated();
    } catch (e: any) { showError(e.message); }
    finally { setSaving(false); }
  };

  const hkDone = async () => {
    setSaving(true);
    try {
      const r = await fetch(`/api/hotel/rooms/${room.id}/housekeeping-done`, { method: 'PATCH', headers, body: '{}' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      showToast('Room marked Available');
      onUpdated();
    } catch (e: any) { showError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-[#0a0f1a] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-3xl font-black text-white">Room {room.roomNumber}</p>
            <p className="text-xs text-slate-400 mt-0.5">{room.type} · Floor {room.floor} · {room.capacity} guests</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-xl ${cfg.bg} ${cfg.border} border`}>
          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
          <span className={`text-sm font-semibold ${cfg.color}`}>{cfg.label}</span>
        </div>
        <p className="text-xl font-bold text-cyan-400 mb-4">{fmt(room.baseRatePerNight)}<span className="text-sm text-slate-500 font-normal">/night</span></p>

        {room.status === 'CLEANING' && (
          <button onClick={hkDone} disabled={saving}
            className="w-full mb-3 py-2.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-xl text-sm font-bold hover:bg-emerald-500/30 transition-all flex items-center justify-center gap-2">
            <Brush size={14} /> Housekeeping Done → Mark Available
          </button>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Change Status</label>
          <select value={newStatus} onChange={e => setNewStatus(e.target.value as RoomStatus)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none">
            {(['AVAILABLE','RESERVED','OCCUPIED','CLEANING','MAINTENANCE'] as RoomStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 outline-none" />
          <button onClick={updateStatus} disabled={saving}
            className="w-full py-2.5 bg-cyan-500 hover:bg-cyan-400 text-black rounded-xl text-sm font-bold transition-all disabled:opacity-40">
            {saving ? 'Saving…' : 'Update Status'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Reservation Detail Modal ─────────────────────────────────────────────────
const ReservationDetailModal = ({ reservation, headers, onClose, showToast, showError, onUpdated }: {
  reservation: HotelReservation; headers: Record<string,string>; onClose: () => void;
  showToast: (m: string) => void; showError: (m: string) => void; onUpdated: () => void;
}) => {
  const [cancelling, setCancelling] = useState(false);

  const cancelReservation = async () => {
    if (!confirm('Cancel this reservation?')) return;
    setCancelling(true);
    try {
      const r = await fetch(`/api/hotel/reservations/${reservation.id}/cancel`, { method: 'PATCH', headers, body: JSON.stringify({ reason: 'Staff cancelled' }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      showToast('Reservation cancelled');
      onUpdated();
    } catch (e: any) { showError(e.message); }
    finally { setCancelling(false); }
  };

  const cfg = RES_STATUS_CONFIG[reservation.status];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-[#0a0f1a] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="font-mono text-sm text-cyan-400">{reservation.reservationRef}</p>
            <p className={`text-xs font-semibold mt-0.5 ${cfg.color}`}>{cfg.label}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-2 text-sm mb-4">
          {[
            ['Room', reservation.Room ? `${reservation.Room.roomNumber} (${reservation.Room.type})` : '-'],
            ['Guest', reservation.Guest?.name || 'Walk-in'],
            ['Phone', reservation.Guest?.phone || '-'],
            ['Check-In', fmtDate(reservation.checkInDate)],
            ['Check-Out', fmtDate(reservation.checkOutDate)],
            ['Nights', `${reservation.nights}`],
            ['Guests', `${reservation.adults} adults, ${reservation.children} children`],
            ['Grand Total', fmt(reservation.grandTotal)],
            ['Payment', reservation.paymentStatus],
          ].map(([l, v]) => (
            <div key={l} className="flex justify-between"><span className="text-slate-400">{l}</span><span className="text-white">{v}</span></div>
          ))}
          {reservation.specialRequests && <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-2 text-xs text-yellow-400">{reservation.specialRequests}</div>}
        </div>
        {['PENDING','CONFIRMED'].includes(reservation.status) && (
          <button onClick={cancelReservation} disabled={cancelling}
            className="w-full py-2.5 bg-red-500/20 border border-red-500/30 text-red-400 rounded-xl text-sm font-semibold hover:bg-red-500/30 transition-all disabled:opacity-40">
            {cancelling ? 'Cancelling…' : 'Cancel Reservation'}
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};

export default HotelDashboard;
